import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function DocumentEditor() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('announcement');
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  const sections = [
    { id: 'announcement', name: '招标公告', icon: 'ri-megaphone-line', status: 'completed' },
    { id: 'instructions', name: '投标人须知', icon: 'ri-file-list-3-line', status: 'completed' },
    { id: 'evaluation', name: '评标办法', icon: 'ri-star-line', status: 'editing' },
    { id: 'contract', name: '投资协议', icon: 'ri-file-text-line', status: 'pending' },
    { id: 'quantity', name: '特许权协议', icon: 'ri-calculator-line', status: 'pending' },
    { id: 'format', name: '投标文件格式', icon: 'ri-layout-line', status: 'pending' },
  ];

  const evaluationMethods = [
    {
      id: 'shortest-period',
      name: '最短特许经营期法',
      description: '适用于投资人招标项目，以特许经营期最短者为优',
      recommended: false,
    },
    {
      id: 'comprehensive',
      name: '综合评估法',
      description: '适用于技术复杂、对质量要求高的项目',
      recommended: true,
    },
  ];

  const scoringItems = [
    {
      category: '商务部分',
      weight: 20,
      color: 'bg-blue-500',
      items: [
        { name: '企业资质', score: 5, description: '施工总承包一级及以上' },
        { name: '类似业绩', score: 8, description: '近5年完成类似项目' },
        { name: '财务状况', score: 4, description: '资产负债率、流动比率' },
        { name: '信誉评价', score: 3, description: '企业信用等级' },
      ],
    },
    {
      category: '技术部分',
      weight: 40,
      color: 'bg-purple-500',
      items: [
        { name: '施工组织设计', score: 15, description: '施工方案的合理性和可行性' },
        { name: '关键技术方案', score: 10, description: '重点难点工程的技术措施' },
        { name: '质量保证措施', score: 8, description: '质量管理体系和保证措施' },
        { name: '安全文明施工', score: 7, description: '安全管理和文明施工措施' },
      ],
    },
    {
      category: '报价部分',
      weight: 40,
      color: 'bg-green-500',
      items: [
        { name: '投标报价', score: 40, description: '采用低价优先评分法' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/agent/dashboard')} className="text-gray-600 hover:text-gray-900 cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">AI辅助招标文件编制工作台</h1>
                <p className="text-sm text-gray-500">某市高速公路建设工程施工总承包招标</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2 text-gray-600 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                <i className="ri-save-line mr-2"></i>
                保存草稿
              </button>
              <button className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 whitespace-nowrap cursor-pointer">
                <i className="ri-shield-check-line mr-2"></i>
                智能审查
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 whitespace-nowrap cursor-pointer">
                <i className="ri-file-download-line mr-2"></i>
                生成文件
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Sidebar - Sections */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">文件章节</h3>
            <div className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg transition-all cursor-pointer ${
                    activeSection === section.id
                      ? 'bg-cyan-50 border-2 border-cyan-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      activeSection === section.id ? 'bg-cyan-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      <i className={`${section.icon} text-lg`}></i>
                    </div>
                    <div className="text-left">
                      <div className={`font-semibold ${activeSection === section.id ? 'text-cyan-900' : 'text-gray-900'}`}>
                        {section.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {section.status === 'completed' && '已完成'}
                        {section.status === 'editing' && '编辑中'}
                        {section.status === 'pending' && '待编制'}
                      </div>
                    </div>
                  </div>
                  {section.status === 'completed' && (
                    <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  )}
                  {section.status === 'editing' && (
                    <i className="ri-edit-circle-fill text-cyan-500 text-xl"></i>
                  )}
                </button>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 p-4 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl">
              <h4 className="text-sm font-bold text-gray-900 mb-3">快捷操作</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowAIAssistant(true)}
                  className="w-full px-4 py-2 bg-white text-cyan-600 rounded-lg hover:bg-cyan-50 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center space-x-2"
                >
                  <i className="ri-sparkling-line"></i>
                  <span>AI智能生成</span>
                </button>
                <button className="w-full px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center space-x-2">
                  <i className="ri-book-line"></i>
                  <span>调用范本库</span>
                </button>
                <button className="w-full px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center space-x-2">
                  <i className="ri-search-line"></i>
                  <span>查询法规</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {/* Evaluation Method Section */}
            {activeSection === 'evaluation' && (
              <div>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">第三部分 评标办法</h2>
                  <p className="text-gray-600">AI将根据项目特点推荐最合适的评标方法和评分标准</p>
                </div>

                {/* Choose Evaluation Method */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <i className="ri-compass-3-line text-cyan-600 mr-2"></i>
                    选择评标方法
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {evaluationMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                          method.recommended
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {method.recommended && (
                          <div className="absolute -top-3 right-4 px-3 py-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs font-bold rounded-full">
                            AI推荐
                          </div>
                        )}
                        <div className="flex items-start space-x-3">
                          <input
                            type="radio"
                            name="evaluation-method"
                            defaultChecked={method.recommended}
                            className="mt-1 w-5 h-5 text-cyan-600 cursor-pointer"
                          />
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 mb-2">{method.name}</h4>
                            <p className="text-sm text-gray-600">{method.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scoring Weight Distribution */}
                <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <i className="ri-pie-chart-line text-purple-600 mr-2"></i>
                      评分权重分配
                    </h3>
                    <button 
                      onClick={() => setShowAIAssistant(true)}
                      className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 whitespace-nowrap cursor-pointer text-sm flex items-center space-x-2"
                    >
                      <i className="ri-sparkling-line"></i>
                      <span>AI优化权重</span>
                    </button>
                  </div>

                  {/* Weight Overview */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600 mb-1">20%</div>
                      <div className="text-sm text-gray-600">商务部分</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600 mb-1">40%</div>
                      <div className="text-sm text-gray-600">技术部分</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-3xl font-bold text-green-600 mb-1">40%</div>
                      <div className="text-sm text-gray-600">报价部分</div>
                    </div>
                  </div>

                  {/* Detailed Scoring Items */}
                  <div className="space-y-6">
                    {scoringItems.map((category, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 ${category.color} rounded-full`}></div>
                            <h4 className="text-lg font-bold text-gray-900">{category.category}</h4>
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">
                              总分 {category.weight}分
                            </span>
                          </div>
                          <button className="text-cyan-600 hover:text-cyan-700 cursor-pointer text-sm flex items-center space-x-1">
                            <i className="ri-add-line"></i>
                            <span>添加评分项</span>
                          </button>
                        </div>

                        <div className="space-y-3">
                          {category.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                  <span className="font-semibold text-gray-900">{item.name}</span>
                                  <span className="px-2 py-1 bg-white text-gray-700 rounded text-sm font-semibold">
                                    {item.score}分
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{item.description}</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-cyan-600 cursor-pointer">
                                  <i className="ri-edit-line"></i>
                                </button>
                                <button className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer">
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technical Scoring Details */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <i className="ri-tools-line text-orange-600 mr-2"></i>
                      技术评分细则（AI智能生成）
                    </h3>
                    <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 whitespace-nowrap cursor-pointer text-sm flex items-center space-x-2">
                      <i className="ri-sparkling-line"></i>
                      <span>生成评分细则</span>
                    </button>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-2xl text-white"></i>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-3">AI将根据项目特点自动生成：</h4>
                        <div className="space-y-2 text-sm text-gray-700">
                          <div className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i>
                            <span><strong>施工组织设计：</strong>总体施工方案、施工进度计划、资源配置计划的合理性评分标准</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i>
                            <span><strong>关键技术方案：</strong>针对高边坡、跨线施工等重难点的技术措施评分标准</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i>
                            <span><strong>质量保证措施：</strong>质量管理体系、检测手段、质量通病防治措施的评分标准</span>
                          </div>
                          <div className="flex items-start space-x-2">
                            <i className="ri-checkbox-circle-fill text-orange-600 mt-0.5"></i>
                            <span><strong>安全文明施工：</strong>安全管理体系、应急预案、文明施工措施的评分标准</span>
                          </div>
                        </div>
                        <div className="mt-4 p-4 bg-white rounded-lg">
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>评分等级示例：</strong>
                          </p>
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="p-2 bg-green-50 border border-green-200 rounded">
                              <div className="font-bold text-green-700">优（9-10分）</div>
                              <div className="text-gray-600 mt-1">方案科学合理，措施具体可行</div>
                            </div>
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                              <div className="font-bold text-blue-700">良（7-8分）</div>
                              <div className="text-gray-600 mt-1">方案较合理，措施较完善</div>
                            </div>
                            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <div className="font-bold text-yellow-700">中（5-6分）</div>
                              <div className="text-gray-600 mt-1">方案基本合理，措施一般</div>
                            </div>
                            <div className="p-2 bg-red-50 border border-red-200 rounded">
                              <div className="font-bold text-red-700">差（0-4分）</div>
                              <div className="text-gray-600 mt-1">方案不合理，措施不完善</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other sections placeholder */}
            {activeSection !== 'evaluation' && (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-text-line text-4xl text-gray-400"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {sections.find(s => s.id === activeSection)?.name}
                </h3>
                <p className="text-gray-600 mb-6">此章节内容正在开发中...</p>
                <button 
                  onClick={() => setShowAIAssistant(true)}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg hover:from-cyan-700 hover:to-blue-700 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-sparkling-line mr-2"></i>
                  AI智能生成内容
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - AI Assistant */}
        {showAIAssistant && (
          <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <i className="ri-sparkling-line text-cyan-600 mr-2"></i>
                  AI智能助手
                </h3>
                <button 
                  onClick={() => setShowAIAssistant(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                {/* AI Suggestions */}
                <div className="p-4 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-2">智能建议</h4>
                      <p className="text-sm text-gray-700">
                        根据项目类型"公路工程"和总投资规模，建议采用综合评估法，技术部分权重不低于40%。
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Templates */}
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">快速模板</h4>
                  <div className="space-y-2">
                    <button className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left cursor-pointer transition-colors">
                      <div className="font-semibold text-gray-900 text-sm mb-1">公路工程标准评标办法</div>
                      <div className="text-xs text-gray-500">适用于一般公路建设项目</div>
                    </button>
                    <button className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left cursor-pointer transition-colors">
                      <div className="font-semibold text-gray-900 text-sm mb-1">桥梁工程评标办法</div>
                      <div className="text-xs text-gray-500">适用于大型桥梁建设项目</div>
                    </button>
                    <button className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left cursor-pointer transition-colors">
                      <div className="font-semibold text-gray-900 text-sm mb-1">隧道工程评标办法</div>
                      <div className="text-xs text-gray-500">适用于隧道建设项目</div>
                    </button>
                  </div>
                </div>

                {/* Related Regulations */}
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">相关法规</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <i className="ri-book-line text-purple-600 mt-0.5"></i>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">《招标投标法》第41条</div>
                          <div className="text-xs text-gray-600 mt-1">评标委员会应当按照招标文件确定的评标标准和方法...</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <i className="ri-book-line text-purple-600 mt-0.5"></i>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">《公路工程标准施工招标文件》</div>
                          <div className="text-xs text-gray-600 mt-1">交通运输部2018版标准范本...</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk Warnings */}
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-3">风险提示</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <i className="ri-alert-line text-orange-600 mt-0.5"></i>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">注意事项</div>
                          <div className="text-xs text-gray-600 mt-1">评分标准应客观量化，避免评委自由裁量权过大</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
